import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LogoShield } from '@/components/LogoShield';
import { toast } from 'sonner';
import { z } from 'zod';
import { ServiceAgreementDialog } from '@/components/portal/ServiceAgreementDialog';
import { MfaVerification } from '@/components/portal/MfaVerification';
import { MfaEnrollment } from '@/components/portal/MfaEnrollment';
import { Helmet } from '@/components/Helmet';
import { type TierType } from '@/components/portal/TierSelection';
import { PortfolioPricingApplicationForm } from '@/components/portal/PortfolioPricingApplicationForm';
import {
  captureCampaignEvent,
} from '@/lib/campaignAnalytics';
import {
  buildPortfolioPricingAnalyticsProps,
  capturePortfolioPricingEvent,
} from '@/lib/portfolioPricingAnalytics';
import {
  DEFAULT_PORTFOLIO_PRICING_FORM,
  normalizePortfolioPricingApplication,
  QUALIFICATION_LABELS,
  QUALIFICATION_OPTIONS,
  type PortfolioPricingApplicationInput,
  validatePortfolioPricingApplication,
  type QualificationPath,
} from '@/lib/portfolioPricing';

const emailSchema = z.string().email('Invalid email address');
const passwordSchema = z.string().min(6, 'Password must be at least 6 characters');

type MfaStep = 'none' | 'verify' | 'enroll';

export default function Auth() {
  const { user, signIn, signUp, loading, signOut, markSessionMfaVerified, isSessionMfaVerified } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const defaultTab = location.pathname.includes('sign-up') ? 'signup' : 'login';
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [signupTier, setSignupTier] = useState<TierType | null>(null);
  const [signupSource, setSignupSource] = useState<string | null>(null);
  const [signupQualificationPath, setSignupQualificationPath] = useState<QualificationPath | null>(null);
  const [signupStep, setSignupStep] = useState<'details' | 'multiSite' | 'portfolio'>('details');
  const [multiSiteApplyForPortfolio, setMultiSiteApplyForPortfolio] = useState(false);
  const [portfolioPricingForm, setPortfolioPricingForm] = useState<PortfolioPricingApplicationInput>(DEFAULT_PORTFOLIO_PRICING_FORM);
  const qualificationViewTrackedRef = useRef(false);
  const signupPathSelectorViewedRef = useRef(false);
  const portfolioApplicationStepViewedRef = useRef(false);
  const [tabValue, setTabValue] = useState(defaultTab);

  // MFA state
  const [mfaStep, setMfaStep] = useState<MfaStep>('none');
  const [mfaMethod, setMfaMethod] = useState<'totp' | 'email' | null>(null);
  const [availableMfaMethods, setAvailableMfaMethods] = useState<string[]>([]);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const pageTitle = defaultTab === 'signup'
    ? 'Create Portal Account | ShadowScan'
    : 'Portal Login | ShadowScan';
  const pageDescription =
    'Access the ShadowScan customer portal to manage protected domains, billing, and security alerts, or create your account to start monitored protection.';
  const redirectAfterAuth = typeof (location.state as any)?.from === 'string'
    ? (location.state as any).from
    : '/portal';

  // Track if MFA process has been initiated to prevent re-triggering
  const [mfaCheckDone, setMfaCheckDone] = useState(false);

  const sessionMfaVerified = isSessionMfaVerified();
  const isLinkedInSignup = Boolean(signupSource && signupSource.includes('linkedin'));
  const wantsPortfolioPricing =
    signupQualificationPath === 'multi_site' && multiSiteApplyForPortfolio;
  const needsSignupFollowUpStep = signupQualificationPath === 'multi_site';

  useEffect(() => {
    // If current session already verified, allow portal access
    if (sessionMfaVerified && user && !loading) {
      navigate(redirectAfterAuth, { replace: true });
      return;
    }

    // Only check MFA status once per login session
    if (user && !loading && mfaStep === 'none' && !mfaCheckDone) {
      setMfaCheckDone(true);
      checkMfaStatus(user.id, user.email || '');
    }
  }, [user, loading, mfaCheckDone, mfaStep, navigate, redirectAfterAuth, sessionMfaVerified]);

  // Reset MFA state when user signs out or session is cleared
  useEffect(() => {
    if (!user) {
      setMfaStep('none');
      setMfaMethod(null);
      setAvailableMfaMethods([]);
      setPendingUserId(null);
      setPendingEmail(null);
      setMfaCheckDone(false);
    }
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const planParam = params.get('plan');
    const sourceParam = params.get('source');
    if (planParam === 'essential' || planParam === 'premium') {
      setSignupTier(planParam);
    } else {
      setSignupTier(null);
    }
    if (sourceParam && sourceParam.trim().length > 0) {
      setSignupSource(sourceParam.trim());
    } else {
      setSignupSource(null);
    }
  }, [location.search]);

  useEffect(() => {
    setTabValue(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    if (tabValue !== 'signup' || signupPathSelectorViewedRef.current) {
      return;
    }

    signupPathSelectorViewedRef.current = true;
    capturePortfolioPricingEvent('portfolio_pricing_signup_path_selector_viewed', {
      page: 'portal_signup',
      step: 'details',
      campaign_source: signupSource,
    });
  }, [signupSource, tabValue]);

  useEffect(() => {
    if (!isLinkedInSignup || tabValue !== 'signup' || qualificationViewTrackedRef.current) {
      return;
    }

    qualificationViewTrackedRef.current = true;
    captureCampaignEvent('linkedin_qualification_viewed', {
      page: 'portal_signup',
      source: signupSource,
    });
  }, [isLinkedInSignup, signupSource, tabValue]);

  useEffect(() => {
    if (tabValue !== 'signup' || signupStep !== 'portfolio' || portfolioApplicationStepViewedRef.current) {
      return;
    }

    portfolioApplicationStepViewedRef.current = true;

    capturePortfolioPricingEvent('portfolio_pricing_application_step_viewed', {
      page: 'portal_signup',
      step: 'portfolio',
      ...buildPortfolioPricingAnalyticsProps({
        submissionSource: 'signup',
        status: 'pending_review',
        campaignSource: signupSource,
        accountKind: 'standard',
      }),
    });
    capturePortfolioPricingEvent('portfolio_pricing_application_started', {
      page: 'portal_signup',
      step: 'portfolio',
      ...buildPortfolioPricingAnalyticsProps({
        submissionSource: 'signup',
        status: 'pending_review',
        campaignSource: signupSource,
        accountKind: 'standard',
      }),
    });

    if (isLinkedInSignup) {
      captureCampaignEvent('linkedin_wholesale_application_started', {
        page: 'portal_signup',
        source: signupSource,
      });
    }
  }, [isLinkedInSignup, signupSource, signupStep, tabValue]);

  useEffect(() => {
    if (tabValue !== 'signup') {
      setSignupStep('details');
      setMultiSiteApplyForPortfolio(false);
      portfolioApplicationStepViewedRef.current = false;
    }
  }, [tabValue]);

  useEffect(() => {
    if (signupQualificationPath !== 'multi_site') {
      setMultiSiteApplyForPortfolio(false);
    }

    if (!wantsPortfolioPricing && signupStep === 'portfolio') {
      setSignupStep(signupQualificationPath === 'multi_site' ? 'multiSite' : 'details');
      portfolioApplicationStepViewedRef.current = false;
    }

    if (signupQualificationPath !== 'multi_site' && signupStep === 'multiSite') {
      setSignupStep('details');
    }

    if (!signupQualificationPath) {
      portfolioApplicationStepViewedRef.current = false;
    }
  }, [signupQualificationPath, signupStep, wantsPortfolioPricing]);

  const ensureProfile = async (userId: string, email: string) => {
    // Ensure a profile row exists (older users may be missing)
    const { data, error } = await supabase
      .from('profiles')
      .select('user_id, mfa_method')
      .eq('user_id', userId)
      .maybeSingle();

    if (data) return data;

    // If not found, create a minimal profile row
    const { data: inserted, error: insertError } = await supabase
      .from('profiles')
      .insert({ user_id: userId, email })
      .select('user_id, mfa_method')
      .maybeSingle();

    if (insertError) {
      throw insertError;
    }
    return inserted;
  };

  const checkMfaStatus = async (userId: string, email: string) => {
    try {
      // Small delay to ensure session is fully established
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const profile = await ensureProfile(userId, email);

      if (profile?.mfa_method) {
        // Parse available methods
        const methods = profile.mfa_method.split(',').filter((m: string) => m.trim().length > 0);
        setAvailableMfaMethods(methods);
        
        // User has MFA enabled, require verification
        setPendingUserId(userId);
        setPendingEmail(email);
        
        // If only one method, use it directly. If multiple, default to first (usually totp)
        const preferredMethod = methods.includes('totp') ? 'totp' : methods[0] as 'totp' | 'email';
        setMfaMethod(preferredMethod);
        setMfaStep('verify');
      } else {
        // No MFA set up, prompt to enroll
        setPendingUserId(userId);
        setPendingEmail(email);
        setAvailableMfaMethods([]);
        setMfaStep('enroll');
      }
    } catch (err) {
      console.error('Error checking MFA status:', err);
      toast.error('Unable to verify MFA status. Please try signing in again.');
      await signOut();
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      emailSchema.parse(loginEmail);
      passwordSchema.parse(loginPassword);
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast.error(err.errors[0].message);
        setIsLoading(false);
        return;
      }
    }

    const { error } = await signIn(loginEmail, loginPassword);
    if (error) {
      toast.error(error.message);
      setIsLoading(false);
    } else {
      // Don't navigate yet - MFA check will happen via useEffect
      setIsLoading(false);
    }
  };

  const updatePortfolioPricingForm = <K extends keyof PortfolioPricingApplicationInput>(
    key: K,
    value: PortfolioPricingApplicationInput[K],
  ) => {
    setPortfolioPricingForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleQualificationChange = (value: QualificationPath) => {
    setSignupQualificationPath(value);
    setMultiSiteApplyForPortfolio(false);
    capturePortfolioPricingEvent('portfolio_pricing_signup_path_selected', {
      page: 'portal_signup',
      selection: value,
      step: 'details',
      ...buildPortfolioPricingAnalyticsProps({
        submissionSource: 'signup',
        campaignSource: signupSource,
        accountKind: 'standard',
      }),
    });

    if (!isLinkedInSignup) {
      return;
    }

    captureCampaignEvent('linkedin_qualification_selected', {
      page: 'portal_signup',
      source: signupSource,
      selection: value,
    });
  };

  const validateSignupDetails = () => {
    if (!signupQualificationPath) {
      throw new Error('Choose the path that best describes how you plan to use ShadowScan.');
    }
  };

  const validateSignupQualification = () => {
    validateSignupDetails();
    if (!wantsPortfolioPricing) {
      return;
    }
    validatePortfolioPricingApplication(portfolioPricingForm);
  };

  const handleSignupClick = (e: React.FormEvent) => {
    e.preventDefault();

    try {
      emailSchema.parse(signupEmail);
      passwordSchema.parse(signupPassword);
      if (!signupName.trim()) throw new Error('Name is required');
      validateSignupDetails();
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast.error(err.errors[0].message);
      } else if (err instanceof Error) {
        toast.error(err.message);
      }
      return;
    }

    if (signupQualificationPath === 'multi_site' && signupStep === 'details') {
      setSignupStep('multiSite');
      return;
    }

    validateSignupQualification();
    setShowAgreement(true);
  };

  const handleSignupConfirm = async () => {
    setShowAgreement(false);
    setIsLoading(true);

    const portfolioPricingApplication = wantsPortfolioPricing
      ? normalizePortfolioPricingApplication(portfolioPricingForm)
      : null;

    const { error } = await signUp(signupEmail, signupPassword, signupName, {
      source: signupSource,
      qualificationPath: signupQualificationPath,
      portfolioPricingApplication,
    });
    if (error) {
      if (error.message.includes('already registered')) {
        toast.error('An account with this email already exists');
      } else {
        toast.error(error.message);
      }
    } else {
      capturePortfolioPricingEvent('portfolio_pricing_signup_completed', {
        page: 'portal_signup',
        selection: signupQualificationPath,
        ...buildPortfolioPricingAnalyticsProps({
          submissionSource: portfolioPricingApplication ? 'signup' : undefined,
          status: portfolioPricingApplication ? 'pending_review' : undefined,
          campaignSource: signupSource,
          accountKind: 'standard',
          approximatePortfolioSize: portfolioPricingApplication?.approximatePortfolioSize,
          currentSiteCount: portfolioPricingApplication?.currentSiteCount,
          expectedSiteCount: portfolioPricingApplication?.expectedSiteCount,
        }),
      });

      if (portfolioPricingApplication) {
        capturePortfolioPricingEvent('portfolio_pricing_application_submitted', {
          page: 'portal_signup',
          qualification_path: signupQualificationPath,
          ...buildPortfolioPricingAnalyticsProps({
            submissionSource: 'signup',
            status: 'pending_review',
            campaignSource: signupSource,
            accountKind: 'standard',
            approximatePortfolioSize: portfolioPricingApplication.approximatePortfolioSize,
            currentSiteCount: portfolioPricingApplication.currentSiteCount,
            expectedSiteCount: portfolioPricingApplication.expectedSiteCount,
          }),
        });
      }

      if (isLinkedInSignup) {
        captureCampaignEvent('linkedin_evaluation_started', {
          page: 'portal_signup',
          source: signupSource,
          path: signupQualificationPath,
        });
        captureCampaignEvent('linkedin_qualification_selected', {
          page: 'portal_signup',
          source: signupSource,
          selection: signupQualificationPath,
        });

        if (portfolioPricingApplication) {
          captureCampaignEvent('linkedin_wholesale_application_submitted', {
            page: 'portal_signup',
            source: signupSource,
          });
        } else {
          captureCampaignEvent('linkedin_standard_path_selected', {
            page: 'portal_signup',
            source: signupSource,
            selection: signupQualificationPath,
          });
        }
      }

      toast.success(
        portfolioPricingApplication
          ? 'Account created and portfolio pricing application submitted. You can now log in.'
          : 'Account created! You can now log in.',
      );
      setSignupStep('details');
      portfolioApplicationStepViewedRef.current = false;
    }
    setIsLoading(false);
  };

  const handleAgreementDecline = () => {
    setShowAgreement(false);
    toast.info('You must accept the service agreement to create an account.');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      emailSchema.parse(forgotEmail);
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast.error(err.errors[0].message);
        setIsLoading(false);
        return;
      }
    }

    const redirectUrl = `${window.location.origin}/portal/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: redirectUrl,
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Password reset email sent! Check your inbox.');
      setShowForgotPassword(false);
      setForgotEmail('');
    }
    setIsLoading(false);
  };

  const handleMfaSuccess = async () => {
    // Refresh session so we have the latest access token and mark MFA for both token and user
    const { data } = await supabase.auth.getSession();
    markSessionMfaVerified(data?.session ?? null);
    toast.success('Logged in successfully');
    navigate(redirectAfterAuth, { replace: true });
  };

  const handleMfaCancel = async () => {
    // Sign out and reset state
    await signOut();
    setMfaStep('none');
    setMfaCheckDone(false);
    setPendingUserId(null);
    setPendingEmail(null);
    setMfaMethod(null);
  };

  const handleMfaEnrollmentComplete = (method: 'totp' | 'email') => {
    markSessionMfaVerified();
    toast.success('Two-factor authentication enabled!');
    // Use replace to prevent back button issues
    navigate(redirectAfterAuth, { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        <div className="text-primary animate-pulse">Loading...</div>
      </div>
    );
  }

  // Show MFA verification if needed
  if (mfaStep === 'verify' && pendingUserId && pendingEmail && mfaMethod) {
    const handleSwitchMethod = (method: 'totp' | 'email') => {
      setMfaMethod(method);
    };

    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        <MfaVerification
          userId={pendingUserId}
          email={pendingEmail}
          mfaMethod={mfaMethod}
          availableMethods={availableMfaMethods}
        onSuccess={handleMfaSuccess}
          onCancel={handleMfaCancel}
          onSwitchMethod={availableMfaMethods.length > 1 ? handleSwitchMethod : undefined}
        />
      </>
    );
  }

  // Show MFA enrollment if user doesn't have MFA set up (mandatory - no skip option)
  if (mfaStep === 'enroll' && pendingUserId && pendingEmail) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        <MfaEnrollment
          userId={pendingUserId}
          email={pendingEmail}
          onComplete={handleMfaEnrollmentComplete}
          showBackButton
          onBack={handleMfaCancel}
        />
      </>
    );
  }

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Helmet>
          <title>Reset Portal Password | ShadowScan</title>
          <meta
            name="description"
            content="Reset your ShadowScan portal password and regain access to your protected domains with a secure recovery link."
          />
        </Helmet>
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        
        <Card className="w-full max-w-md cyber-card relative z-10">
          <Link 
            to="/" 
            className="absolute top-4 left-4 p-2 text-primary/70 hover:text-primary border border-primary/30 hover:border-primary/60 rounded transition-all hover:shadow-[0_0_15px_hsl(185_80%_50%/0.3)]"
            aria-label="Back to Home"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <CardHeader className="text-center pt-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <LogoShield className="h-8 w-8" />
              <span className="text-2xl font-mono font-bold text-foreground">ShadowScan</span>
            </div>
            <CardTitle className="text-xl">Reset Password</CardTitle>
            <CardDescription>Enter your email to receive a password reset link</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="bg-muted/50"
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setShowForgotPassword(false)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      <Card
        className={`w-full ${
          tabValue === 'signup'
            ? signupStep === 'portfolio'
              ? 'max-w-2xl'
              : 'max-w-xl'
            : 'max-w-md'
        } cyber-card relative z-10`}
      >
        <Link 
          to="/" 
          className="absolute top-4 left-4 p-2 text-primary/70 hover:text-primary border border-primary/30 hover:border-primary/60 rounded transition-all hover:shadow-[0_0_15px_hsl(185_80%_50%/0.3)]"
          aria-label="Back to Home"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <CardHeader className="text-center pt-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <LogoShield className="h-8 w-8" />
            <span className="text-2xl font-mono font-bold text-foreground">ShadowScan</span>
          </div>
          <CardTitle className="text-xl">Customer Portal</CardTitle>
          <CardDescription>Manage your protected domains</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={tabValue} onValueChange={setTabValue} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="bg-muted/50"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Password</Label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-primary hover:text-primary/80 transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="bg-muted/50 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Logging in...' : 'Login'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignupClick} className="space-y-4">
                {signupStep === 'details' ? (
                  <>
                    <div className="space-y-1">
                      <h3 className="text-lg font-semibold text-foreground">Create your account</h3>
                      <p className="text-sm text-muted-foreground">
                        Start with your account details, then choose how you&apos;ll use ShadowScan.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Full name</Label>
                      <Input
                        id="signup-name"
                        type="text"
                        value={signupName}
                        onChange={(e) => setSignupName(e.target.value)}
                        placeholder="John Doe"
                        required
                        className="bg-muted/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        value={signupEmail}
                        onChange={(e) => setSignupEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        className="bg-muted/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="signup-password"
                          type={showPassword ? 'text' : 'password'}
                          value={signupPassword}
                          onChange={(e) => setSignupPassword(e.target.value)}
                          placeholder="••••••••"
                          required
                          className="bg-muted/50 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
                      <div className="space-y-2">
                        <Label>How will you use ShadowScan?</Label>
                        <p className="text-sm text-muted-foreground">
                          If you manage multiple WordPress sites, you can continue on the standard path and optionally apply for reviewed portfolio pricing on the next step.
                        </p>
                      </div>
                      <RadioGroup
                        value={signupQualificationPath || ''}
                        onValueChange={(value) => handleQualificationChange(value as QualificationPath)}
                        className="mt-4 space-y-3"
                      >
                        {QUALIFICATION_OPTIONS.map((option) => (
                          <label
                            key={option.value}
                            htmlFor={`qualification-${option.value}`}
                            className="flex cursor-pointer items-start gap-3 rounded-md border border-border/60 bg-background/60 p-3 transition hover:border-primary/30"
                          >
                            <RadioGroupItem value={option.value} id={`qualification-${option.value}`} className="mt-0.5" />
                            <div className="space-y-1">
                              <div className="text-sm font-medium text-foreground">{option.label}</div>
                              <div className="text-xs leading-relaxed text-muted-foreground">{option.description}</div>
                            </div>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>

                    {signupQualificationPath ? (
                      <p className="text-sm text-muted-foreground">
                        You&apos;re signing up as someone {QUALIFICATION_LABELS[signupQualificationPath]}. Your first domains include a 7-day free trial - no charge until the trial ends.
                        {signupQualificationPath === 'multi_site'
                          ? ' You can optionally apply for reviewed portfolio pricing on the next step.'
                          : ''}
                      </p>
                    ) : null}

                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading
                        ? 'Creating account...'
                        : needsSignupFollowUpStep
                          ? 'Continue'
                          : 'Create account'}
                    </Button>
                  </>
                ) : signupStep === 'multiSite' ? (
                  <>
                    <div className="space-y-2 rounded-lg border border-primary/20 bg-primary/5 p-4">
                      <Label className="text-base">Managing multiple WordPress sites</Label>
                      <p className="text-sm text-muted-foreground">
                        You can continue on the standard self-serve path, or apply for reviewed portfolio pricing if that may be relevant to your account.
                      </p>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        Standard self-serve remains available while review is pending.
                      </p>
                    </div>

                    <div className="rounded-md border border-border/60 bg-muted/10 p-3 text-sm text-muted-foreground">
                      Account details for <span className="font-medium text-foreground">{signupName || 'this account'}</span>
                      {signupEmail ? ` · ${signupEmail}` : ''}. You can keep moving on the standard path now and apply later from Billing if you prefer.
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row">
                      <Button
                        type="button"
                        variant="outline"
                        className="sm:flex-1"
                        onClick={() => {
                          setSignupStep('details');
                          setMultiSiteApplyForPortfolio(false);
                        }}
                        disabled={isLoading}
                      >
                        Back
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        className="sm:flex-1"
                        onClick={() => {
                          setMultiSiteApplyForPortfolio(true);
                          setSignupStep('portfolio');
                        }}
                        disabled={isLoading}
                      >
                        Apply for portfolio pricing
                      </Button>
                      <Button type="submit" className="sm:flex-1" disabled={isLoading}>
                        {isLoading ? 'Creating account...' : 'Continue with standard path'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2 rounded-lg border border-primary/20 bg-primary/5 p-4">
                      <Label className="text-base">Apply for portfolio pricing</Label>
                      <p className="text-sm text-muted-foreground">
                        Tell us a bit about your portfolio so ShadowScan can review whether a portfolio pricing path is suitable for your account.
                      </p>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        Standard self-serve remains available while review is pending.
                      </p>
                    </div>

                    <PortfolioPricingApplicationForm
                      value={portfolioPricingForm}
                      onChange={updatePortfolioPricingForm}
                    />

                    <div className="rounded-md border border-border/60 bg-muted/10 p-3 text-sm text-muted-foreground">
                      Account details for <span className="font-medium text-foreground">{signupName || 'this account'}</span>
                      {signupEmail ? ` · ${signupEmail}` : ''}. Review is required before any separate pricing path is approved.
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row">
                      <Button
                        type="button"
                        variant="outline"
                        className="sm:flex-1"
                        onClick={() => {
                          setSignupStep(signupQualificationPath === 'multi_site' ? 'multiSite' : 'details');
                          setMultiSiteApplyForPortfolio(false);
                          portfolioApplicationStepViewedRef.current = false;
                        }}
                        disabled={isLoading}
                      >
                        Back
                      </Button>
                      <Button type="submit" className="sm:flex-1" disabled={isLoading}>
                        {isLoading ? 'Creating account...' : 'Create account and apply'}
                      </Button>
                    </div>
                  </>
                )}
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <ServiceAgreementDialog
        open={showAgreement}
        onAccept={handleSignupConfirm}
        onDecline={handleAgreementDecline}
        clientName={signupName}
        clientEmail={signupEmail}
        selectedTier={signupTier || undefined}
      />
    </div>
  );
}
